// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Bookmark, BookmarkPlus, Check, Trash2, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { decodeFilters } from "@/lib/filter-params";
import {
  encodePresetValue,
  type FilterPreset,
  findPresetByName,
  findPresetByValue,
  loadPresets,
  normalizePresetName,
  PRESET_NAME_MAX_LENGTH,
  removePreset,
  savePresets,
  upsertPreset,
} from "@/lib/filter-presets";

// The icon-only trigger sits in the table's toolbar beside Tools, so it borrows the active styling of
// the filter-bar controls rather than their labelled shape: the applied preset is ticked in the menu.
const ACTIVE_TRIGGER_STYLE =
  "bg-sidebar-accent-hover hover:bg-sidebar-accent border-sidebar-accent hover:border-sidebar-primary text-sidebar-accent-foreground hover:text-sidebar-accent-foreground dark:bg-sidebar-accent dark:border-sidebar-accent dark:hover:bg-sidebar-accent-hover dark:hover:border-sidebar-primary";

interface FilterPresetsProps<T extends Record<string, unknown>> {
  /** The page's `DataTable` storageKey — presets live per page, beside its column visibility. */
  storageKey: string;
  /** The page's `FILTER_DEFAULTS`, which defines both the key order and what counts as "no filter". */
  defaults: T;
  /** The page's current filter state, in the same shape it hands to `encodeFilters`. */
  current: T;
  /**
   * Apply a decoded filter state. `page` and `pageSize` arrive as the page's defaults — a preset
   * never carries them, so the call site resets to the first page and keeps its own page size.
   */
  onApply: (filters: T) => void;
}

export function FilterPresets<T extends Record<string, unknown>>({
  storageKey,
  defaults,
  current,
  onApply,
}: FilterPresetsProps<T>) {
  const { t } = useTranslation("back-office");
  // Read once on mount: every later change goes through `persist`, which saves as it sets.
  const [presets, setPresets] = useState<FilterPreset[]>(() => loadPresets(storageKey));
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");

  const currentValue = useMemo(() => encodePresetValue(current, defaults), [current, defaults]);
  const activePreset = findPresetByValue(presets, currentValue);
  // Nothing differs from the defaults, so there is no combination worth naming.
  const nothingToSave = currentValue === "";

  const persist = useCallback(
    (next: FilterPreset[]) => {
      setPresets(next);
      savePresets(storageKey, next);
    },
    [storageKey],
  );

  const trimmedName = normalizePresetName(name);
  const existing = findPresetByName(presets, trimmedName);
  const canSave = trimmedName !== "" && !nothingToSave;

  function openSaveDialog() {
    setName(activePreset?.name ?? "");
    setSaveOpen(true);
  }

  function handleSave() {
    if (!canSave) return;
    persist(upsertPreset(presets, trimmedName, currentValue));
    setSaveOpen(false);
    toast.success(t("dataTable.presets.saved", { name: trimmedName }));
  }

  function handleDelete(preset: FilterPreset) {
    persist(removePreset(presets, preset.name));
    toast.success(t("dataTable.presets.deleted", { name: preset.name }));
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label={t("dataTable.presets.label")}
            className={activePreset ? ACTIVE_TRIGGER_STYLE : undefined}
          >
            <Bookmark className={`h-4 w-4 ${activePreset ? "fill-sidebar-primary/25" : ""}`} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={-36} className="dropdown-overlap w-64">
          <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
            <Bookmark className="h-3.5 w-3.5" />
            {t("dataTable.presets.label")}
          </DropdownMenuLabel>
          {presets.length === 0 ? (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">{t("dataTable.presets.empty")}</div>
          ) : (
            presets.map((preset) => {
              const isActive = preset.name === activePreset?.name;
              return (
                <DropdownMenuItem
                  key={preset.name}
                  className="gap-2"
                  onSelect={() => onApply(decodeFilters(preset.value, defaults))}
                  aria-current={isActive || undefined}
                >
                  <span className="flex-1 truncate">{preset.name}</span>
                  {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  {/* A span, not a <button>: DropdownMenuItem is the interactive element here. */}
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={t("dataTable.presets.delete", { name: preset.name })}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(preset);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                </DropdownMenuItem>
              );
            })
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={nothingToSave} onSelect={openSaveDialog}>
            <BookmarkPlus className="h-4 w-4" />
            {t("dataTable.presets.save")}
          </DropdownMenuItem>
          {activePreset && (
            // Clearing a preset is the same act as clearing the filter bar: back to the page's defaults.
            <DropdownMenuItem onSelect={() => onApply({ ...defaults })}>
              <X className="h-4 w-4" />
              {t("dataTable.presets.clear")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent
          id="dialog-save-filter-preset"
          className="sm:max-w-[30rem] p-0 gap-0 overflow-hidden flex flex-col"
        >
          <div className="flex flex-row">
            <WizardPane icon={BookmarkPlus} />
            <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
              <DialogHeader>
                <DialogTitle>{t("dataTable.presets.saveTitle")}</DialogTitle>
                <DialogDescription>{t("dataTable.presets.saveDescription")}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2">
                <Label htmlFor="filter-preset-name">{t("dataTable.presets.nameLabel")}</Label>
                <Input
                  id="filter-preset-name"
                  value={name}
                  maxLength={PRESET_NAME_MAX_LENGTH}
                  placeholder={t("dataTable.presets.namePlaceholder")}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSave();
                    }
                  }}
                  autoFocus
                />
                {existing && <p className="text-xs text-muted-foreground">{t("dataTable.presets.overwriteHint")}</p>}
              </div>
            </div>
          </div>
          <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              {t("buttons.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={!canSave}>
              <BookmarkPlus className="h-4 w-4" />
              {existing ? t("dataTable.presets.overwrite") : t("buttons.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
